import React, { useEffect, useRef, useState } from 'react';
import {
  List,
  Radio,
  Input,
  Spin,
  Empty,
  Tag,
  Typography,
  Tree,
  Alert,
  Pagination,
} from 'antd';
import {
  FileOutlined,
  CheckCircleFilled,
  WarningOutlined,
  FolderOutlined,
  FolderOpenOutlined,
  ClockCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { CustomService } from '@core/services/custom.service';
import {
  FileSelectionMode,
  FilePreviewItem,
} from '@core/@models/ApiManualModel';
import { IDirectoryTree } from '@core/@models/DirectoryServiceModel';
import { matchesWildcard } from '../utils/codeGenerators';
import { finalize } from 'rxjs/operators';
import { DataNode } from 'antd/lib/tree';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const { Text } = Typography;

interface FileSelectorProps {
  folderId: number | null;
  value?: string;
  onChange: (
    filename: string,
    isPattern: boolean,
    selectedKeys?: string[],
    /** When includeSub: actual file count in output (backend returns all matching-by-name) */
    effectiveFileCount?: number
  ) => void;
  mode?: 'get' | 'delete';
  includeSub?: boolean;
  isRecursive?: boolean;
  folderPath?: string;
  multiple?: boolean;
  /** Begin date in DD/MM/YYYY format for filtering display */
  beginDate?: string;
  /** End date in DD/MM/YYYY format for filtering display */
  endDate?: string;
}

export interface FileListItem {
  id: number;
  name: string;
  size: string;
  modified: string;
}

interface FileListResponse {
  content: FileListItem[];
  totalElements: number;
}

interface SubfolderFile {
  name: string;
  path: string;
  size?: string;
  modified?: string;
  isMatch?: boolean;
}

interface FolderWithFiles {
  folderName: string;
  folderPath: string;
  files: SubfolderFile[];
}

interface TreeNodeData extends DataNode {
  isFile?: boolean;
  isMatch?: boolean;
  filePath?: string;
}

/** Format a date string for display */
const formatDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const d = dayjs(dateStr);
  if (d.isValid()) return d.format('DD/MM/YYYY HH:mm');
  return dateStr;
};

/** Check if a file's modified date falls within a range */
const isFileInDateRange = (
  modified: string | undefined,
  beginDate?: string,
  endDate?: string
): boolean => {
  if (!beginDate && !endDate) return true;
  if (!modified) return true;
  const fileDate = dayjs(modified);
  if (!fileDate.isValid()) return true;
  if (beginDate) {
    const begin = dayjs(beginDate, 'DD/MM/YYYY');
    if (begin.isValid() && fileDate.isBefore(begin, 'day')) return false;
  }
  if (endDate) {
    const end = dayjs(endDate, 'DD/MM/YYYY');
    if (end.isValid() && fileDate.isAfter(end, 'day')) return false;
  }
  return true;
};

const MAX_FILENAME_LENGTH = 255;
const MAX_FILE_LIST_SIZE = 100;

/** Number of subfolder groups to show per page when includeSub is enabled */
const SUBFOLDER_PAGE_SIZE = 5;

/** Displayable file item combining current folder and subfolder files */
interface DisplayableFile {
  name: string;
  size?: string;
  modified?: string;
  /** Subfolder path if from a subfolder, empty if from current folder */
  subfolderPath?: string;
}

export const FileSelector: React.FC<FileSelectorProps> = ({
  folderId,
  onChange,
  mode = 'get',
  includeSub = false,
  isRecursive = false,
  folderPath = '',
  multiple = false,
  beginDate,
  endDate,
}) => {
  const [selectionMode, setSelectionMode] = useState<FileSelectionMode>('list');
  const [files, setFiles] = useState<FileListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pattern, setPattern] = useState('');
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);

  const [subfolderLoading, setSubfolderLoading] = useState(false);
  const [subfolderFiles, setSubfolderFiles] = useState<SubfolderFile[]>([]);
  const [foldersWithFiles, setFoldersWithFiles] = useState<FolderWithFiles[]>(
    []
  );
  const [, setTreeExpandedKeys] = useState<React.Key[]>([]);
  const [subfolderPage, setSubfolderPage] = useState(1);

  const hasDateFilter = !!(beginDate || endDate);

  useEffect(() => {
    if (folderId) {
      fetchFiles();
    } else {
      setFiles([]);
    }
  }, [folderId]);

  useEffect(() => {
    setSelectedFile('');
    setSelectedFiles([]);
    setPattern('');
    setSubfolderPage(1);
    onChange('', false, [], undefined);
  }, [folderId, includeSub]);

  const fetchFiles = () => {
    if (!folderId) return;

    setLoading(true);
    CustomService.getData<FileListResponse>('/file-services/select', {
      folderId,
      size: 100,
    })
      .pipe(finalize(() => setLoading(false)))
      .subscribe({
        next: (result) => {
          setFiles(result.content || []);
        },
        error: () => {
          setFiles([]);
        },
      });
  };

  const subfolderFetchRef = useRef<string | null>(null);

  useEffect(() => {
    if (!includeSub || !folderId) {
      setSubfolderFiles([]);
      setFoldersWithFiles([]);
      setTreeExpandedKeys([]);
      subfolderFetchRef.current = null;
      return;
    }

    const requestId = `${folderId}-${folderPath || ''}`;
    subfolderFetchRef.current = requestId;

    const fetchSubfolderFilesRecursively = async (
      parentFolderId: number,
      parentPath: string
    ) => {
      setSubfolderLoading(true);
      try {
        const subfolders = await CustomService.getData<IDirectoryTree[]>(
          `/file-services/expand?folderId=${parentFolderId}`
        ).toPromise();

        if (subfolderFetchRef.current !== requestId) return;

        if (!subfolders || subfolders.length === 0) {
          setSubfolderFiles([]);
          setFoldersWithFiles([]);
          return;
        }

        const normalizedCurrentPath = (parentPath || '').replace(/\/$/, '');
        const allFiles: SubfolderFile[] = [];
        const folders: FolderWithFiles[] = [];
        const expandKeys: React.Key[] = [];
        const seenFolderIds = new Set<number>();

        for (const folder of subfolders) {
          if (seenFolderIds.has(folder.id)) continue;
          if (!folder.name || !String(folder.name).trim()) continue;
          seenFolderIds.add(folder.id);

          const subPath = `${normalizedCurrentPath}/${folder.name}`.replace(
            /\/+/g,
            '/'
          );
          if (subPath === normalizedCurrentPath) continue;

          expandKeys.push(`folder-${folder.id}`);

          try {
            const filesResult = await CustomService.getData<FileListResponse>(
              '/file-services/select',
              { folderId: folder.id, size: 100 }
            ).toPromise();

            if (subfolderFetchRef.current !== requestId) return;

            const folderFiles: SubfolderFile[] = [];
            if (filesResult?.content) {
              filesResult.content.forEach((file: FileListItem) => {
                const fileData = {
                  name: file.name,
                  path: subPath,
                  size: file.size,
                  modified: file.modified,
                };
                allFiles.push(fileData);
                folderFiles.push(fileData);
              });
            }

            folders.push({
              folderName: folder.name,
              folderPath: subPath,
              files: folderFiles,
            });
          } catch (e) {
            console.error(`Error fetching files from ${subPath}`, e);
          }
        }

        if (subfolderFetchRef.current !== requestId) return;
        setSubfolderFiles(allFiles);
        setFoldersWithFiles(folders);
        setTreeExpandedKeys(expandKeys);
      } catch (e) {
        console.error('Error fetching subfolders', e);
        if (subfolderFetchRef.current === requestId) {
          setSubfolderFiles([]);
          setFoldersWithFiles([]);
        }
      } finally {
        if (subfolderFetchRef.current === requestId) {
          setSubfolderLoading(false);
        }
      }
    };

    fetchSubfolderFilesRecursively(folderId, folderPath || '');

    return () => {
      subfolderFetchRef.current = null;
    };
  }, [includeSub, folderId, folderPath]);

  const filteredFiles = hasDateFilter
    ? files.filter((f) => isFileInDateRange(f.modified, beginDate, endDate))
    : files;

  const filteredSubfolderFiles = hasDateFilter
    ? subfolderFiles.filter((f) =>
        isFileInDateRange(f.modified, beginDate, endDate)
      )
    : subfolderFiles;

  const buildDisplayFiles = (): DisplayableFile[] => {
    const result: DisplayableFile[] = filteredFiles.map((f) => ({
      name: f.name,
      size: f.size,
      modified: f.modified,
      subfolderPath: '',
    }));

    if (includeSub) {
      filteredSubfolderFiles.forEach((sf) => {
        result.push({
          name: sf.name,
          size: sf.size,
          modified: sf.modified,
          subfolderPath: sf.path,
        });
      });
    }

    return result;
  };

  const displayFiles = buildDisplayFiles();
  const totalCurrentFiles = filteredFiles.length;
  const totalSubFiles = includeSub ? filteredSubfolderFiles.length : 0;
  const filteredOutCount =
    files.length -
    filteredFiles.length +
    (includeSub ? subfolderFiles.length - filteredSubfolderFiles.length : 0);

  // Build tree data for pattern preview
  const buildTreeData = (): TreeNodeData[] => {
    const treeNodes: TreeNodeData[] = [];

    const currentFolderNode: TreeNodeData = {
      key: 'current-folder',
      title: (
        <span style={{ fontWeight: 'bold' }}>
          <FolderOpenOutlined style={{ color: '#faad14', marginRight: 8 }} />
          {folderPath || '/'}
        </span>
      ),
      children: filteredFiles.map((file, idx) => {
        const isMatch = pattern ? matchesWildcard(file.name, pattern) : false;
        return {
          key: `current-file-${idx}`,
          title: (
            <span
              style={{
                color: isMatch
                  ? mode === 'delete'
                    ? '#ff4d4f'
                    : '#52c41a'
                  : undefined,
                fontWeight: isMatch ? 'bold' : 'normal',
              }}
            >
              <FileOutlined style={{ marginRight: 8 }} />
              {file.name}
              {isMatch && (
                <Tag
                  color={mode === 'delete' ? 'red' : 'green'}
                  style={{ marginLeft: 8 }}
                >
                  {mode === 'delete' ? 'DELETE' : 'MATCH'}
                </Tag>
              )}
            </span>
          ),
          isLeaf: true,
          isFile: true,
          isMatch,
        } as TreeNodeData;
      }),
    };
    treeNodes.push(currentFolderNode);

    if (includeSub) {
      foldersWithFiles.forEach((folder, folderIdx) => {
        const folderNode: TreeNodeData = {
          key: `subfolder-${folderIdx}`,
          title: (
            <span>
              <FolderOutlined style={{ color: '#faad14', marginRight: 8 }} />
              {folder.folderName}
            </span>
          ),
          children: folder.files
            .filter((f) => isFileInDateRange(f.modified, beginDate, endDate))
            .map((file, fileIdx) => {
              const isMatch = pattern
                ? matchesWildcard(file.name, pattern)
                : false;
              return {
                key: `subfolder-${folderIdx}-file-${fileIdx}`,
                title: (
                  <span
                    style={{
                      color: isMatch
                        ? mode === 'delete'
                          ? '#ff4d4f'
                          : '#52c41a'
                        : undefined,
                      fontWeight: isMatch ? 'bold' : 'normal',
                    }}
                  >
                    <FileOutlined style={{ marginRight: 8 }} />
                    {file.name}
                    {isMatch && (
                      <Tag
                        color={mode === 'delete' ? 'red' : 'green'}
                        style={{ marginLeft: 8 }}
                      >
                        {mode === 'delete' ? 'DELETE' : 'MATCH'}
                      </Tag>
                    )}
                  </span>
                ),
                isLeaf: true,
                isFile: true,
                isMatch,
              } as TreeNodeData;
            }),
        };
        treeNodes.push(folderNode);
      });
    }

    return treeNodes;
  };

  const handleModeChange = (newMode: FileSelectionMode) => {
    setSelectionMode(newMode);
    if (newMode === 'list') {
      setPattern('');
      if (multiple) {
        const fileListStr = selectedKeysToFileList(selectedFiles);
        onChange(
          fileListStr,
          false,
          includeSub ? getEffectiveSelectedKeys(fileListStr) : undefined,
          includeSub ? getEffectiveFileCount(fileListStr) : undefined
        );
      } else {
        onChange(selectedFile, false);
      }
    } else {
      setSelectedFile('');
      setSelectedFiles([]);
      onChange(pattern, true);
    }
  };

  /** Unique key for a file when includeSub: subfolderPath/name or just name for current folder */
  const getFileKey = (file: DisplayableFile): string =>
    includeSub && file.subfolderPath
      ? `${file.subfolderPath}/${file.name}`
      : file.name;

  /** Convert selected keys to API fileList: filenames only (duplicates preserved for same-name files in different folders) */
  const selectedKeysToFileList = (keys: string[]): string =>
    keys.map((k) => (k.includes('/') ? k.split('/').pop() ?? k : k)).join(',');

  /** When includeSub: backend returns ALL files matching each name - get keys of all files in output */
  const getEffectiveSelectedKeys = (fileListStr: string): string[] => {
    if (!includeSub || !fileListStr.trim()) return [];
    const names = fileListStr
      .split(',')
      .map((n) => n.trim())
      .filter(Boolean);
    const uniqueNames = names.filter((n, i) => names.indexOf(n) === i);
    return uniqueNames.flatMap((name) =>
      displayFiles.filter((f) => f.name === name).map((f) => getFileKey(f))
    );
  };

  const getEffectiveFileCount = (fileListStr: string): number =>
    getEffectiveSelectedKeys(fileListStr).length;

  const handleFileSelect = (fileKey: string) => {
    if (multiple) {
      const isDeselect = selectedFiles.includes(fileKey);
      if (!isDeselect && selectedFiles.length >= MAX_FILE_LIST_SIZE) {
        return; // Enforce backend limit
      }
      const updated = isDeselect
        ? selectedFiles.filter((f) => f !== fileKey)
        : [...selectedFiles, fileKey];
      setSelectedFiles(updated);
      const fileListStr = selectedKeysToFileList(updated);
      onChange(
        fileListStr,
        false,
        includeSub ? getEffectiveSelectedKeys(fileListStr) : updated,
        includeSub ? getEffectiveFileCount(fileListStr) : undefined
      );
    } else {
      const filename = fileKey.includes('/')
        ? fileKey.split('/').pop() ?? fileKey
        : fileKey;
      setSelectedFile(filename);
      onChange(
        filename,
        false,
        undefined,
        includeSub ? getEffectiveFileCount(filename) : undefined
      );
    }
  };

  const handlePatternChange = (newPattern: string) => {
    setPattern(newPattern);
    onChange(newPattern, true);
  };

  // Validate pattern against backend limits
  const getPatternWarning = (): string | null => {
    if (!pattern) return null;
    const parts = pattern
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length > MAX_FILE_LIST_SIZE) {
      return `Too many patterns (${parts.length}). Maximum is ${MAX_FILE_LIST_SIZE}.`;
    }
    const longPart = parts.find((p) => p.length > MAX_FILENAME_LENGTH);
    if (longPart) {
      return `Pattern "${longPart.substring(
        0,
        20
      )}..." exceeds ${MAX_FILENAME_LENGTH} characters.`;
    }
    return null;
  };

  const patternWarning = getPatternWarning();

  // Filter files for pattern match preview
  const getMatchingFiles = (): (FilePreviewItem & { path?: string })[] => {
    if (!pattern || selectionMode !== 'pattern') return [];

    const currentFolderFiles = filteredFiles.map((file) => ({
      name: file.name,
      isMatch: matchesWildcard(file.name, pattern),
      size: file.size,
      path: folderPath,
    }));

    if (includeSub && subfolderFiles.length > 0) {
      const subFiles = subfolderFiles
        .filter((f) => isFileInDateRange(f.modified, beginDate, endDate))
        .map((file) => ({
          name: file.name,
          isMatch: matchesWildcard(file.name, pattern),
          size: file.size,
          path: file.path,
        }));
      return [...currentFolderFiles, ...subFiles];
    }

    return currentFolderFiles;
  };

  const matchingFiles = getMatchingFiles();
  const matchCount = matchingFiles.filter((f) => f.isMatch).length;
  const subfolderMatchCount = includeSub
    ? matchingFiles.filter((f) => f.isMatch && f.path !== folderPath).length
    : 0;
  const isDelete = mode === 'delete';

  // Get relative folder name from a subfolder path
  const getRelativeFolderName = (subPath: string): string => {
    if (!subPath) return '';
    const parts = subPath.split('/');
    return parts[parts.length - 1] || subPath;
  };

  // Group display files by subfolder for visual clarity
  const currentFolderDisplayFiles = displayFiles.filter(
    (f) => !f.subfolderPath
  );
  const subFolderGroups: {
    folderName: string;
    files: DisplayableFile[];
  }[] = [];
  if (includeSub) {
    const normalizedFolderPath = (folderPath || '').replace(/\/$/, '');
    const groupMap = new Map<string, DisplayableFile[]>();
    displayFiles
      .filter((f) => {
        const p = (f.subfolderPath || '').replace(/\/$/, '');
        return p && p !== normalizedFolderPath;
      })
      .forEach((f) => {
        const key = f.subfolderPath || '';
        if (!groupMap.has(key)) groupMap.set(key, []);
        const group = groupMap.get(key);
        if (group) group.push(f);
      });
    groupMap.forEach((groupFiles, path) => {
      subFolderGroups.push({
        folderName: getRelativeFolderName(path),
        files: groupFiles,
      });
    });
  }

  return (
    <div className="file-selector">
      {(multiple || mode === 'delete') && (
        <Radio.Group
          value={selectionMode}
          onChange={(e) => handleModeChange(e.target.value)}
          style={{ marginBottom: 16 }}
        >
          <Radio value="list">Select from list</Radio>
          <Radio value="pattern">Use pattern (wildcard)</Radio>
        </Radio.Group>
      )}

      {selectionMode === 'list' ? (
        <Spin spinning={loading || subfolderLoading}>
          {displayFiles.length > 0 ? (
            <>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 4,
                }}
              >
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {displayFiles.length} file(s)
                  {multiple &&
                    ` · ${selectedFiles.length}/${MAX_FILE_LIST_SIZE} selected`}
                  {includeSub &&
                    totalSubFiles > 0 &&
                    ` (${totalCurrentFiles} in current, ${totalSubFiles} in subfolders)`}
                  {filteredOutCount > 0 &&
                    ` · ${filteredOutCount} hidden by date filter`}
                </Text>
              </div>

              {/* Current folder files */}
              {currentFolderDisplayFiles.length > 0 && (
                <>
                  {includeSub && totalSubFiles > 0 && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '4px 8px',
                        background: '#fafafa',
                        borderRadius: '4px 4px 0 0',
                        border: '1px solid #f0f0f0',
                        borderBottom: 'none',
                        fontSize: 11,
                        color: '#666',
                      }}
                    >
                      <FolderOpenOutlined style={{ color: '#faad14' }} />
                      <span style={{ fontWeight: 500 }}>Current folder</span>
                    </div>
                  )}
                  <List
                    className="file-list"
                    size="small"
                    bordered
                    dataSource={currentFolderDisplayFiles}
                    renderItem={(file) => {
                      const fileKey = getFileKey(file);
                      const isSelected = multiple
                        ? selectedFiles.includes(fileKey)
                        : selectedFile === file.name;
                      return (
                        <List.Item
                          className={`file-item ${
                            isSelected ? 'selected' : ''
                          }`}
                          onClick={() => handleFileSelect(fileKey)}
                          style={{ padding: '8px 12px' }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              width: '100%',
                              gap: 10,
                            }}
                          >
                            {isSelected ? (
                              <CheckCircleFilled
                                style={{
                                  color: '#52c41a',
                                  fontSize: 16,
                                  flexShrink: 0,
                                }}
                              />
                            ) : (
                              <FileOutlined
                                style={{
                                  fontSize: 16,
                                  color: '#bbb',
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontWeight: isSelected ? 500 : 'normal',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  lineHeight: '20px',
                                }}
                              >
                                {file.name}
                              </div>
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 16,
                                flexShrink: 0,
                                fontSize: 12,
                                color: '#999',
                              }}
                            >
                              {file.modified && (
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                  }}
                                >
                                  <ClockCircleOutlined
                                    style={{ fontSize: 11 }}
                                  />
                                  {formatDate(file.modified)}
                                </span>
                              )}
                              {file.size && (
                                <span
                                  style={{ minWidth: 50, textAlign: 'right' }}
                                >
                                  {file.size}
                                </span>
                              )}
                            </div>
                          </div>
                        </List.Item>
                      );
                    }}
                    style={{
                      maxHeight: includeSub && totalSubFiles > 0 ? 180 : 240,
                      overflow: 'auto',
                      ...(includeSub && totalSubFiles > 0
                        ? { borderRadius: '0 0 4px 4px' }
                        : {}),
                    }}
                  />
                </>
              )}

              {/* Subfolder file groups (paginated) */}
              {includeSub &&
                (() => {
                  const totalSubfolders = subFolderGroups.length;
                  const startIdx = (subfolderPage - 1) * SUBFOLDER_PAGE_SIZE;
                  const paginatedGroups = subFolderGroups.slice(
                    startIdx,
                    startIdx + SUBFOLDER_PAGE_SIZE
                  );
                  return (
                    <>
                      {paginatedGroups.map((group, gIdx) => (
                        <div key={startIdx + gIdx} style={{ marginTop: 8 }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '4px 8px',
                              background: '#fafafa',
                              borderRadius: '4px 4px 0 0',
                              border: '1px solid #f0f0f0',
                              borderBottom: 'none',
                              fontSize: 11,
                              color: '#666',
                            }}
                          >
                            <FolderOutlined style={{ color: '#faad14' }} />
                            <span style={{ fontWeight: 500 }}>
                              {group.folderName}
                            </span>
                            <span style={{ color: '#bbb' }}>
                              ({group.files.length} file
                              {group.files.length !== 1 ? 's' : ''})
                            </span>
                          </div>
                          <List
                            className="file-list"
                            size="small"
                            bordered
                            dataSource={group.files}
                            renderItem={(file) => {
                              const fileKey = getFileKey(file);
                              const isSelected = multiple
                                ? selectedFiles.includes(fileKey)
                                : selectedFile === file.name;
                              return (
                                <List.Item
                                  className={`file-item ${
                                    isSelected ? 'selected' : ''
                                  }`}
                                  onClick={() => handleFileSelect(fileKey)}
                                  style={{ padding: '8px 12px' }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      width: '100%',
                                      gap: 10,
                                    }}
                                  >
                                    {isSelected ? (
                                      <CheckCircleFilled
                                        style={{
                                          color: '#52c41a',
                                          fontSize: 16,
                                          flexShrink: 0,
                                        }}
                                      />
                                    ) : (
                                      <FileOutlined
                                        style={{
                                          fontSize: 16,
                                          color: '#bbb',
                                          flexShrink: 0,
                                        }}
                                      />
                                    )}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div
                                        style={{
                                          fontWeight: isSelected
                                            ? 500
                                            : 'normal',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          lineHeight: '20px',
                                        }}
                                      >
                                        {file.name}
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 16,
                                        flexShrink: 0,
                                        fontSize: 12,
                                        color: '#999',
                                      }}
                                    >
                                      {file.modified && (
                                        <span
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 4,
                                          }}
                                        >
                                          <ClockCircleOutlined
                                            style={{ fontSize: 11 }}
                                          />
                                          {formatDate(file.modified)}
                                        </span>
                                      )}
                                      {file.size && (
                                        <span
                                          style={{
                                            minWidth: 50,
                                            textAlign: 'right',
                                          }}
                                        >
                                          {file.size}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </List.Item>
                              );
                            }}
                            style={{
                              maxHeight: 160,
                              overflow: 'auto',
                              borderRadius: '0 0 4px 4px',
                            }}
                          />
                        </div>
                      ))}
                      {totalSubfolders > SUBFOLDER_PAGE_SIZE && (
                        <div
                          style={{
                            marginTop: 12,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 8,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Pagination
                            size="small"
                            current={subfolderPage}
                            pageSize={SUBFOLDER_PAGE_SIZE}
                            total={totalSubfolders}
                            onChange={(page) => setSubfolderPage(page)}
                            showSizeChanger={false}
                            showTotal={(total, range) =>
                              `Subfolders ${range[0]}-${range[1]} of ${total}`
                            }
                          />
                        </div>
                      )}
                    </>
                  );
                })()}
            </>
          ) : (
            <Empty
              description={
                folderId
                  ? hasDateFilter && files.length > 0
                    ? 'No files match the selected date range'
                    : 'No files in this folder'
                  : 'Select a folder first'
              }
              style={{ padding: '20px 0' }}
            />
          )}
        </Spin>
      ) : (
        <div className="pattern-input-section">
          <Input
            placeholder="*.csv, report_*.pdf, *.pdf,*.doc"
            value={pattern}
            onChange={(e) => handlePatternChange(e.target.value)}
            style={{
              marginBottom: 8,
              ...(patternWarning ? { borderColor: '#faad14' } : {}),
            }}
          />
          {patternWarning ? (
            <div style={{ marginBottom: 12 }}>
              <Text type="warning" style={{ fontSize: 11, color: '#faad14' }}>
                <WarningOutlined style={{ marginRight: 4 }} />
                {patternWarning}
              </Text>
            </div>
          ) : (
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Supports comma-separated patterns (max {MAX_FILE_LIST_SIZE}).
                e.g. *.pdf,*.doc matches both PDF and DOC files.
              </Text>
            </div>
          )}

          {/* Pattern Examples */}
          <div className="pattern-examples" style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Pattern Examples:
            </Text>
            <div style={{ marginTop: 4 }}>
              {[
                '*.csv',
                'report_*.csv',
                '*_2026*',
                '*.pdf,*.doc',
                '*.xlsx,*.csv',
              ].map((ex) => (
                <Tag
                  key={ex}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handlePatternChange(ex)}
                >
                  {ex}
                </Tag>
              ))}
            </div>
          </div>

          {/* Include Sub Alert */}
          {includeSub && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                subfolderLoading
                  ? 'Loading files from subdirectories...'
                  : `Include subdirectories enabled - ${filteredSubfolderFiles.length} file(s) found in ${foldersWithFiles.length} subfolder(s)`
              }
            />
          )}

          {/* Preview matching files - Tree Structure */}
          {pattern && (filteredFiles.length > 0 || subfolderFiles.length > 0) && (
            <Spin spinning={subfolderLoading}>
              <div
                className={`file-preview-tree ${
                  isDelete ? 'delete-preview' : 'get-preview'
                }`}
              >
                <div className="preview-header">
                  {isDelete ? (
                    <WarningOutlined className="preview-header-icon" />
                  ) : (
                    <SearchOutlined className="preview-header-icon" />
                  )}
                  <div className="preview-header-content">
                    <Text strong className="preview-title">
                      {isDelete
                        ? 'WARNING: Files to be DELETED'
                        : `Preview: Files matching "${pattern}"`}
                    </Text>
                    {includeSub && (
                      <Tag color="blue" className="preview-subfolders-tag">
                        +Subfolders
                      </Tag>
                    )}
                  </div>
                </div>

                <div className="tree-preview-container">
                  <Tree
                    showLine={{ showLeafIcon: false }}
                    showIcon={false}
                    defaultExpandAll
                    selectable={false}
                    treeData={buildTreeData()}
                    className="preview-tree"
                  />
                </div>

                <div className="preview-summary">
                  {isDelete ? (
                    <span className="preview-summary-text preview-summary-danger">
                      <WarningOutlined />{' '}
                      {isRecursive ? (
                        <>Files matching pattern: {pattern} will be DELETED</>
                      ) : (
                        <>
                          {matchCount} file(s) will be DELETED
                          {subfolderMatchCount > 0 && (
                            <span className="preview-subfolder-count">
                              {' '}
                              ({subfolderMatchCount} from subfolders)
                            </span>
                          )}
                        </>
                      )}
                    </span>
                  ) : (
                    <span className="preview-summary-text preview-summary-success">
                      {matchCount} file(s) will be downloaded
                      {matchCount > 1 ? ' (ZIP)' : ''}
                      {subfolderMatchCount > 0 && (
                        <span className="preview-subfolder-count">
                          {' '}
                          ({subfolderMatchCount} from subfolders)
                        </span>
                      )}
                    </span>
                  )}
                </div>
              </div>
            </Spin>
          )}
        </div>
      )}
    </div>
  );
};
